import { useTheme } from "next-themes"
import { useCallback, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Image } from "~/components/reusable/Image"
import { Loading } from "~/components/reusable/Loading"
import { cn } from "~/utils/helpers"

import ProductPlaceholder from "~/images/image_placeholder.png"
import ProductPlaceholderDark from "~/images/image_placeholder_dark.png"
import ProductPlaceholderSmall from "~/images/image_placeholder_small.png"
import ProductPlaceholderSmallDark from "~/images/image_placeholder_small_dark.png"

const PREVIEW_SIZE = 300
const PREVIEW_OFFSET = 8

interface ProductImageProps {
  url?: string | null
  alt: string
  width?: number
  withHoverPreview?: boolean
  placeholderSize?: "small" | "large"
  priority?: boolean
  objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down"
  tile?: boolean
}

export default function ProductImage({
  url,
  alt,
  width = 32,
  withHoverPreview = true,
  placeholderSize = "small",
  priority = false,
  objectFit = "cover",
  tile = false,
}: ProductImageProps) {
  const { resolvedTheme } = useTheme()
  const containerRef = useRef<HTMLDivElement>(null)
  const [previewPos, setPreviewPos] = useState<{ top: number; left: number } | null>(null)
  const [imageLoaded, setImageLoaded] = useState(false)

  const placeholderSrc = useMemo(() => {
    const isDark = resolvedTheme === "dark"
    if (placeholderSize === "small") {
      return isDark ? ProductPlaceholderSmallDark : ProductPlaceholderSmall
    }
    return isDark ? ProductPlaceholderDark : ProductPlaceholder
  }, [resolvedTheme, placeholderSize])

  const maxWidth = useMemo(() => {
    return width
      ? `${width}px`
      : "(max-width: 639px) 32px, (max-width: 767px) 64px, (max-width: 1023px) 128px, 256px"
  }, [width])
  const sizesAttr = tile && width ? `${width * 2}px` : maxWidth

  const showPreview = withHoverPreview && !!url

  const onMouseEnter = useCallback(() => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const spaceRight = window.innerWidth - rect.right
    const spaceBelow = window.innerHeight - rect.bottom

    let left = rect.right + PREVIEW_OFFSET
    if (spaceRight < PREVIEW_SIZE + PREVIEW_OFFSET) {
      left = rect.left - PREVIEW_SIZE - PREVIEW_OFFSET
    }

    let top = rect.top
    if (spaceBelow < PREVIEW_SIZE) {
      top = Math.max(PREVIEW_OFFSET, rect.bottom - PREVIEW_SIZE)
    }

    setPreviewPos({ top, left })
  }, [])

  const onMouseLeave = useCallback(() => {
    setPreviewPos(null)
    setImageLoaded(false)
  }, [])

  return (
    <>
      <div
        ref={containerRef}
        style={{ maxWidth }}
        className={cn(
          "rounded-[8px] shadow-card-rest relative aspect-square overflow-hidden w-full border-none",
          tile && "product-image-tile",
        )}
        data-photo={tile && !!url?.trim() ? "" : undefined}
        {...(showPreview && { onMouseEnter, onMouseLeave })}
      >
        <Image
          src={url?.trim() || placeholderSrc}
          alt={alt}
          width={width}
          height={width}
          sizes={sizesAttr}
          priority={priority}
          objectFit={objectFit}
          className="border-none"
        />
      </div>
      {previewPos &&
        createPortal(
          <div
            style={{
              top: previewPos.top,
              left: previewPos.left,
              width: PREVIEW_SIZE + 8,
              height: PREVIEW_SIZE + 8,
            }}
            // Mounts/unmounts rather than toggling a state attribute, so it takes the bare
            // enter utility. Scales from its centre: the preview flips sides to fit the
            // viewport and doesn't track which side it landed on.
            className="fixed z-50 pointer-events-none rounded-[12px] shadow-flyout border border-base bg-base p-1 overlay-enter origin-center"
          >
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loading size="lg" className="text-fg-muted" />
              </div>
            )}
            <img
              src={url!}
              alt={alt}
              onLoad={() => setImageLoaded(true)}
              className={cn(
                "rounded-[8px] object-contain transition-opacity duration-100",
                imageLoaded ? "opacity-100" : "opacity-0",
              )}
              style={{ width: PREVIEW_SIZE, height: PREVIEW_SIZE }}
            />
          </div>,
          document.body,
        )}
    </>
  )
}
